/**
 * PasswordService
 *
 * Password reset and change flows, session revocation.
 * Owns: Password-related Edge Functions (request-password-reset, confirm-password-reset, revoke-sessions-and-audit)
 *       supabase.auth.signInWithPassword (for re-auth), supabase.auth.updateUser (for password changes)
 *
 * Handles: requestPasswordReset, confirmPasswordReset, changePassword with session revocation
 * Does NOT handle: login flows (AuthService), email verification (EmailChangeService)
 */

import { supabase } from '@/lib/supabase';
import { AuthErrorCode } from '@/types/auth';
import type {
  PasswordResetRequest,
  PasswordResetConfirmRequest,
  ChangePasswordRequest,
  AuthOperationResponse
} from '@/types/auth';

/**
 * PasswordService
 * Password reset and change operations
 */
class PasswordService {
  /**
   * Request password reset email
   *
   * Calls Edge Function with rate limiting protection.
   * Rate limits: 5/min per IP, 30/day per email hash
   *
   * @param request - Email address for password reset
   * @returns Operation result (always returns success message for security)
   */
  async requestPasswordReset(request: PasswordResetRequest): Promise<AuthOperationResponse> {
    console.log('🔑 PasswordService: Password reset request for', request.email);

    try {
      // Call Edge Function with rate limiting (WU3 Task 3 - Phase 3B)
      const { data, error } = await supabase.functions.invoke('request-password-reset', {
        body: { email: request.email }
      });

      if (error) {
        console.error('❌ PasswordService: Edge Function error:', error);

        // Check for rate limit errors
        if (error.message?.includes('rate limit') || error.message?.includes('Too many')) {
          return {
            success: false,
            message: 'Too many password reset requests. Please try again later.'
          };
        }

        // Check for service unavailable (Redis failure)
        if (error.message?.includes('temporarily unavailable')) {
          return {
            success: false,
            message: 'Service temporarily unavailable. Please try again later.'
          };
        }

        // For other errors, return generic success (don't reveal internals)
        return {
          success: true,
          message:
            'If an account with this email exists, you will receive password reset instructions shortly.'
        };
      }

      console.log('✅ PasswordService: Password reset request processed successfully');

      // Return response from Edge Function
      return {
        success: data?.success ?? true,
        message:
          data?.message ??
          'If an account with this email exists, you will receive password reset instructions shortly.'
      };
    } catch (error) {
      console.error('❌ PasswordService: Password reset request error:', error);
      // Still return success (don't reveal errors)
      return {
        success: true,
        message:
          'If an account with this email exists, you will receive password reset instructions shortly.'
      };
    }
  }

  /**
   * Confirm password reset with token
   *
   * Validates token and updates password via Edge Function admin API.
   *
   * @param request - Reset token and new password
   * @returns Operation result
   */
  async confirmPasswordReset(request: PasswordResetConfirmRequest): Promise<AuthOperationResponse> {
    console.log('🔐 PasswordService: Password reset confirmation');

    try {
      // Validate passwords match
      if (request.newPassword !== request.confirmPassword) {
        return {
          success: false,
          error: 'VALIDATION_ERROR' as AuthErrorCode,
          message: 'Passwords do not match'
        };
      }

      // Call Edge Function to reset password
      // Edge Function validates token and updates password via admin API
      const { data: resetResult, error: resetError } = await supabase.functions.invoke('reset-password', {
        body: {
          token: request.resetToken,
          newPassword: request.newPassword
        }
      });

      if (resetError) {
        console.error('❌ PasswordService: Password reset Edge Function error:', resetError);
        return {
          success: false,
          error: 'RESET_FAILED' as AuthErrorCode,
          message: 'Failed to reset password. Please try again.'
        };
      }

      if (!resetResult?.success) {
        // Map error codes from Edge Function
        const errorCode = resetResult?.error || 'RESET_FAILED';
        const errorMessages: Record<string, string> = {
          INVALID_TOKEN: 'Invalid or expired password reset link',
          TOKEN_EXPIRED: 'This password reset link has expired. Please request a new one.',
          WEAK_PASSWORD: resetResult?.message || 'Password does not meet requirements',
          UPDATE_FAILED: 'Failed to update password. Please try again.',
          MISSING_PARAMETERS: 'Invalid request. Please try again.',
          INTERNAL_ERROR: 'An unexpected error occurred. Please try again.'
        };

        return {
          success: false,
          error: errorCode as AuthErrorCode,
          message: errorMessages[errorCode] || 'Password reset failed'
        };
      }

      console.log('✅ PasswordService: Password reset successful');
      return {
        success: true,
        message: 'Password reset successful. You can now log in with your new password.'
      };
    } catch (error) {
      console.error('❌ PasswordService: Password reset confirmation error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred. Please try again.'
      };
    }
  }

  /**
   * Change password for authenticated user
   *
   * Performs re-authentication, updates password, and revokes other sessions.
   *
   * @param request - Current password, new password, and user info
   * @returns Operation result
   */
  async changePassword(request: ChangePasswordRequest & { userId: string; userEmail: string }): Promise<AuthOperationResponse> {
    console.log('🔒 PasswordService: Password change request');

    try {
      // Validate passwords match
      if (request.newPassword !== request.confirmPassword) {
        return {
          success: false,
          error: 'VALIDATION_ERROR' as AuthErrorCode,
          message: 'Passwords do not match'
        };
      }

      // Attempt to reauthenticate with current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: request.userEmail,
        password: request.currentPassword
      });

      if (signInError) {
        console.error('❌ PasswordService: Current password verification failed:', signInError);
        return {
          success: false,
          error: 'INVALID_CREDENTIALS' as AuthErrorCode,
          message: 'Current password is incorrect'
        };
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: request.newPassword
      });

      if (updateError) {
        console.error('❌ PasswordService: Password update failed:', updateError);

        // Handle specific Supabase errors
        if (updateError.message.includes('Password should be')) {
          return {
            success: false,
            error: 'WEAK_PASSWORD' as AuthErrorCode,
            message: updateError.message
          };
        }

        return {
          success: false,
          error: 'UPDATE_FAILED' as AuthErrorCode,
          message: 'Failed to update password. Please try again.'
        };
      }

      console.log('✅ PasswordService: Password changed successfully');

      // Client-side session revocation (belt-and-suspenders - ChatGPT Consult 3)
      let clientRevocationFailed = false;
      try {
        await supabase.auth.signOut({ scope: 'others' });
        console.log('✅ PasswordService: Client-side session revocation successful');
      } catch (revocationError) {
        console.error('❌ PasswordService: Client-side session revocation failed:', revocationError);
        clientRevocationFailed = true;
      }

      // Call centralized revoke-sessions-and-audit Edge Function
      // Consultant: Use user JWT only (NO internal secret from browser - security risk!)
      // ChatGPT Consult 3: No ip_address field - derived server-side only
      try {
        const { error: edgeFunctionError } = await supabase.functions.invoke('revoke-sessions-and-audit', {
          body: {
            user_id: request.userId,
            change_method: 'authenticated',
            user_agent: navigator.userAgent
          }
          // Note: Authorization header automatically added by supabase.functions.invoke()
          // Edge Function verifies user.id matches body.user_id (prevents abuse)
        });

        if (edgeFunctionError) {
          console.error('❌ PasswordService: Edge Function call failed:', edgeFunctionError);
        } else {
          console.log('✅ PasswordService: revoke-sessions-and-audit Edge Function called successfully');
        }
      } catch (edgeFunctionCallError) {
        console.error(
          '❌ PasswordService: Failed to call revoke-sessions-and-audit Edge Function:',
          edgeFunctionCallError
        );
        // Non-blocking: password change still succeeds
      }

      // NOTE: Password changed notification email is sent by revoke-sessions-and-audit Edge Function
      // (server-to-server call to send-email, not from browser)

      // Return appropriate message based on client revocation status
      // Consult 1: Show soft notice if client-side revocation failed
      const message = clientRevocationFailed
        ? "We changed your password but couldn't confirm other devices were logged out. We'll keep trying in the background."
        : "Password changed successfully. You've been kept signed in — other devices have been logged out.";

      return {
        success: true,
        message
      };
    } catch (error) {
      console.error('❌ PasswordService: Password change error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while changing password'
      };
    }
  }
}

// Export singleton instance
export const passwordService = new PasswordService();
